import { SaveCredentialsParams } from './../../../../base/entities/BaseEntities';
import { DxValidationGroupComponent } from 'devextreme-angular';
import { Component, ViewChild } from '@angular/core';
import { BaseState, BaseEntity, BaseEntityResponse, CredentialExternalInfo, BaseDataResponse, WebAPICredentialInfo, WebAPICredential, WebAPIAllowedIP } from '@base/entities/BaseEntities';
import { BasePageComponent } from '@base/component/BasePageComponent';
import { EnumPageDisplayModes, EnumNotificationDeliveryOptions, EnumMFAMethods } from '@core/framework/sysenums.enum';
import { ServiceBagService, ServiceBagServices } from '@base/component/ServiceBag';
import { LoginEndpoint } from '@core/endpoints/login.endpoint';
import { Utils } from '@m4u-angular/core/helpers/Utils';
import { EnumSystemOptions } from '@m4u-angular/core/framework/systemoptions.enum';
import { WelcomeUserDialogComponent } from './welcome-user-dialog/welcome-user-dialog.component';


class State extends BaseState
{
    Credential: WebAPICredential = new WebAPICredential;
    WebAPIAllowedIPs: WebAPIAllowedIP[] = [];
    CredentialInfo: CredentialExternalInfo = new CredentialExternalInfo;
    IsExists: boolean = false;
    IsLockedOut: boolean = false;
    IsSupportedUserWelcome: boolean = false;
    MFAMethodList: any[] = [];
}

@Component({
    providers: ServiceBagServices,
    selector: 'm4u-passwordmanage',
    templateUrl: './passwordmanage.component.html',
    styleUrls: ['./passwordmanage.component.scss']
})
export class PasswordManageComponent extends BasePageComponent<BaseEntity, State>
{
    @ViewChild('validationGroup') validationGroup: DxValidationGroupComponent;
    @ViewChild('welcomeUserDialog') welcomeUserDialog: WelcomeUserDialogComponent;

    constructor(A: ServiceBagService, private loginEndpoint: LoginEndpoint) {
        super(A, 'PasswordManageComponent', BaseEntity, State, EnumPageDisplayModes.Window);
    }

    override OnInitComponent() {
        this.LoadCredentials();
        this.LoadCredentialsInfo();
    }

    override OnInitTitle()
    {
        var userName = this.State.CredentialInfo && this.State.CredentialInfo.DisplayUserName;
        return userName && `${this.GetDefaultTitle()} - ${userName}`;
    }

    LoadCredentials()
    {
        this.Do(this.loginEndpoint.GetCredentials(this.QueryParams.CredentialID, this.QueryParams.Type), false, (response: BaseDataResponse<WebAPICredentialInfo>) =>
        {
            this.State.IsExists = true;
            if (response.Data)
            {
                this.State.Credential.UserName = response.Data.UserName;
                this.State.Credential.Type = response.Data.Type;
                this.State.Credential.IsActive = response.Data.IsActive;
                this.State.Credential.IsPendingWelcome = response.Data.IsPendingWelcome;
                this.State.Credential.MFAMethod = response.Data.MFAMethod;
                this.State.Credential.IsRestrictByIP = response.Data.IsRestrictByIP;
                this.State.Credential.IsExcludeFromMFA = response.Data.IsExcludeFromMFA;
                this.State.IsLockedOut = response.Data.IsLockedOut;
                this.State.IsSupportedUserWelcome = response.Data.IsSupportedUserWelcome;
                this.State.WebAPIAllowedIPs = response.Data.WebAPIAllowedIPs;
            }
        }, () =>
        {
            this.State.IsExists = false;
            this.State.Credential.MFAMethod = EnumMFAMethods.None;
            this.OnGenerateRandomPassword();
        });
    }

    LoadCredentialsInfo()
    {
        this.Do(this.loginEndpoint.GetExternalInfo(this.QueryParams.CredentialID, this.QueryParams.Type), false, (response: BaseEntityResponse<CredentialExternalInfo>) => 
        {
            if (response.Entity)
            {
                this.State.CredentialInfo = response.Entity;
                this.NotifyReloadTitle();
                if (!this.State.IsExists) {
                    this.State.Credential.UserName = this.State.CredentialInfo.Email;
                    this.State.Credential.Type = this.QueryParams.Type;
                }
                this.OnInitMFAMethodsList();
            }
        });
    }

    OnGenerateRandomPassword()
    {
        this.Do(this.loginEndpoint.GenerateRandomPassword(), false, (response: BaseDataResponse<string>) => this.State.Credential.Password = response.Data);
    }

    OnSaveChanges()
    {
        if (!this.IsValid()) { return; }

        this.ValidateValidationGroup(this.validationGroup).then((isValid) => {
            if (isValid) {
                const params = new SaveCredentialsParams();
                params.CredentialID = this.QueryParams.CredentialID;
                params.UserName = this.State.Credential.UserName;
                params.Password = this.State.Credential.Password;
                params.Type = this.State.Credential.Type;
                params.MFAMethod = this.State.Credential.MFAMethod;
                params.IsRestrictByIP = this.State.Credential.IsRestrictByIP;
                params.IsExcludeFromMFA = this.State.Credential.IsExcludeFromMFA;
                params.WebAPIAllowedIPs = this.State.WebAPIAllowedIPs;
                params.IsActive = this.State.Credential.IsActive;

                this.Do(this.loginEndpoint.SaveCredentials(params), true, () => {
                    if (!this.State.IsExists) {
                        this.LoadCredentials();
                        return;
                    }
                    this.NotifyEntitySaved(this.State.Credential, WebAPICredential);
                    this.QueryParams.ReturnUrl 
                        ? this.A.Navigator.Navigate([this.QueryParams.ReturnUrl])
                        : this.A.RouteHistoryService.GoBack();
                });
            }
        });
    }

    OnMFAMethodChanged () {
        if (this.State.Credential.MFAMethod === EnumMFAMethods.None) {
            this.State.Credential.IsExcludeFromMFA = false;
        } else {
            this.State.Credential.IsRestrictByIP = false;
        }
    }

    OnIsRestrictByIPChanged() {
        if (!this.State.Credential.IsRestrictByIP) { this.State.Credential.IsExcludeFromMFA = false; }
    }

    IsMFAMethodNone() {
        return this.State.Credential.MFAMethod === EnumMFAMethods.None;
    }

    OnIPEditorPreparing(e) {
        if (e.dataField === 'IPRangeStart') {
            const defaultValueChangeHandler = e.editorOptions.onValueChanged;

            e.editorOptions.onValueChanged = (args) => {
                defaultValueChangeHandler(args);

                if (!e.row.data.IPRangeEnd) {
                    e.component.cellValue(e.row.rowIndex, 'IPRangeEnd', e.row.data.IPRangeStart);
                }
            };
        }
    }

    OnUnlockUser()
    {
        this.State.IsLockedOut = false;
        this.Do(this.loginEndpoint.UnlockCredentials(this.QueryParams.CredentialID), true);
    }

    OnWelcomeUser() {
        this.welcomeUserDialog.CredentialID = this.QueryParams.CredentialID;
        this.welcomeUserDialog.CredentialInfo = this.State.CredentialInfo;
        this.welcomeUserDialog.ShowDialogShort(() => {});
    }
 
    private IsValid() {
        if (this.State.Credential.IsRestrictByIP || this.State.Credential.IsExcludeFromMFA) {
            if (this.State.WebAPIAllowedIPs.length === 0) {
                this.UI.StopMessage(this.RC('msgIPRangesIsEmpty'));

                return false;
            } else {
                if (this.State.WebAPIAllowedIPs.some(x => Utils.IsEmpty(x.IPRangeStart) && Utils.IsEmpty(x.IPRangeEnd))) {
                    this.UI.StopMessage(this.RC('msgIPRangeIsEmpty'));

                    return false;
                }
            }
        }

        return true;
    }

    private OnInitMFAMethodsList() {
        this.State.MFAMethodList = this.LIST('MFAMethods').filter(x => {
            if (x.Value === EnumMFAMethods.None) return true;

            // Check MFAMethod Email
            if (x.Value === EnumMFAMethods.Email) {
                // If Email is filled then add MFAMethod to the list
                if(this.State.CredentialInfo.Email) return true;
                // If old MFAMethod is Email, set it as disabled and add to the list
                if(this.State.Credential.MFAMethod === EnumMFAMethods.Email) {
                    (x as any).disabled = true;
                    return true;
                }
            }

            // Check MFAMethod SMS
            if (x.Value === EnumMFAMethods.SMS) {
                // If Phone is filled then add MFAMethod to the list
                if(this.State.CredentialInfo.Phone) return true;
                // If old MFAMethod is SMS, set it as disabled and add to the list
                if(this.State.Credential.MFAMethod === EnumMFAMethods.SMS) {
                    (x as any).disabled = true;
                    return true;
                }
            }

            return false;
        });
    }

    //GetPasswordTooltipLabel(): string {
    //    return this.RC('msgPasswordComplexityRequirement').replace('{0}', this.SystemOption(EnumSystemOptions.PasswordComplexityLen))
    //        .replace('{1}', this.SystemOption(EnumSystemOptions.PasswordComplexityMinDigits))
    //        .replace('{2}', this.SystemOption(EnumSystemOptions.PasswordComplexityMinChars));
    //}
}
